/* Shared attribution and hidden fields for native Shopify inquiry forms. */
(() => {
  'use strict';
  if (window.restopackInquiry) return;
  const script = document.currentScript;
  const DAY = 86400000;
  const WINDOW_DAYS = 90;
  const STORAGE_KEY = 'restopack_attribution_v2';
  const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'matchtype'];
  const GOOGLE = ['gclid', 'wbraid', 'gbraid'];
  const CLICK = [...GOOGLE, 'fbclid'];
  const normalHost = host => host.replace(/^www\./, '').toLowerCase();
  const ownHosts = [location.hostname, script?.dataset.shopDomain, script?.dataset.permanentDomain].filter(Boolean).map(normalHost);
  const isOwn = url => ownHosts.includes(normalHost(url.hostname));
  const parseUrl = value => { try { return new URL(value, location.origin); } catch { return null; } };
  const currentUrl = new URL(location.href);
  const ref = document.referrer ? parseUrl(document.referrer) : null;
  const fresh = (value, max = WINDOW_DAYS * DAY) => value && Number.isFinite(value.at) && Date.now() >= value.at && Date.now() - value.at < max;
  const read = (storage, key) => { try { return JSON.parse(window[storage].getItem(key) || 'null'); } catch { return null; } };
  const write = (storage, key, value) => { try { window[storage].setItem(key, JSON.stringify(value)); } catch { /* Continue in memory. */ } };
  let state = read('localStorage', STORAGE_KEY);
  if (!state || typeof state !== 'object' || Array.isArray(state)) state = {};
  ['first', 'last', 'google', 'facebook'].forEach(key => { if (!fresh(state[key])) delete state[key]; });
  const hasSignals = url => [...UTM, ...CLICK].some(key => url.searchParams.get(key));
  // Recover an unrecorded same-store landing. Never mix two campaigns, or
  // treat parameters on an external referrer as this store's campaign.
  const recoverReferrer = !hasSignals(currentUrl) && ref && isOwn(ref) && hasSignals(ref) && !state.last;
  const landing = recoverReferrer ? ref : currentUrl;
  const touch = {
    at: Date.now(), landing_page: landing.href, referrer: document.referrer || 'Direct',
    device: matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop'
  };
  [...UTM, ...CLICK].forEach(key => { touch[key] = landing.searchParams.get(key) || ''; });
  const googleEvidence = GOOGLE.filter(key => touch[key]);
  const external = ref && !isOwn(ref);
  touch.source = touch.utm_source || (googleEvidence.length ? 'google' : (external ? ref.hostname : 'direct'));
  touch.medium = touch.utm_medium || (googleEvidence.length ? 'paid' : (external ? 'referral' : '(none)'));
  touch.source_evidence = touch.utm_source ? (recoverReferrer ? 'same_site_referrer_utm_source' : 'utm_source') :
    (googleEvidence.length ? (recoverReferrer ? 'same_site_referrer_' : '') + googleEvidence.join('+') : (external ? 'referrer' : 'none'));
  // Preserve Leafpackage semantics: fbclid alone is not evidence of paid traffic.
  if (!state.first) state.first = touch;
  if (!state.last || hasSignals(landing) || external) state.last = touch;
  if (googleEvidence.length) {
    state.google = { at: touch.at };
    GOOGLE.forEach(key => { state.google[key] = touch[key]; });
  }
  if (touch.fbclid) state.facebook = { at: touch.at, fbclid: touch.fbclid };
  write('localStorage', STORAGE_KEY, state);

  const forms = new WeakMap();
  const products = new Map();
  function attribution() {
    const fields = { attribution_window_days: String(WINDOW_DAYS) };
    ['first', 'last'].forEach(prefix => {
      const record = fresh(state[prefix]) ? state[prefix] : {};
      [...UTM, 'landing_page', 'referrer', 'device', 'source', 'medium', 'source_evidence'].forEach(key => {
        fields[prefix + '_' + key] = record[key] || '';
      });
      fields[prefix + '_touch_at'] = record.at ? new Date(record.at).toISOString() : '';
    });
    const google = fresh(state.google) ? state.google : {};
    GOOGLE.forEach(key => { fields[key] = google[key] || ''; });
    fields.google_click_at = google.at ? new Date(google.at).toISOString() : '';
    fields.fbclid = fresh(state.facebook) ? state.facebook.fbclid : '';
    return fields;
  }
  function input(form, key) { return form.querySelector('input[name="contact[' + key + ']"]'); }
  function set(form, key, value) { const field = input(form, key); if (field) field.value = value == null ? '' : String(value); }
  function newId() {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return 'RP-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-' + Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  function readInquiry(key) {
    let value = read('sessionStorage', key);
    if (!value) {
      try {
        const cookie = document.cookie.split('; ').find(part => part.startsWith(encodeURIComponent(key) + '='));
        if (cookie) value = JSON.parse(decodeURIComponent(cookie.slice(cookie.indexOf('=') + 1)));
      } catch { /* Use a new ID when storage is unavailable. */ }
    }
    return fresh(value, DAY) && /^RP-\d{8}-[a-f0-9]{32}$/.test(value.id || '') ? value : null;
  }
  function saveInquiry(info) {
    write('sessionStorage', info.key, info.record);
    if (read('sessionStorage', info.key)?.id === info.record.id) return;
    try {
      document.cookie = encodeURIComponent(info.key) + '=' + encodeURIComponent(JSON.stringify(info.record)) + '; Path=/; Max-Age=86400; SameSite=Lax; Secure';
    } catch { /* The in-memory record still works for this page. */ }
  }
  function initialize(form) {
    if (forms.has(form)) return forms.get(form);
    const marker = form.querySelector('[data-rp-inquiry-fields]');
    if (!marker) return null;
    const scope = form.closest('.shopify-section')?.id || 'page';
    const index = Array.from(document.forms).filter(f => f.id === form.id).indexOf(form);
    const key = 'restopack_inquiry_v1_' + location.pathname + '_' + scope + '_' + form.id + '_' + index;
    let record = readInquiry(key);
    if (marker.dataset.posted === 'true') {
      if (record) {
        record.status = 'completed';
        saveInquiry({ key, record });
      }
      forms.set(form, { completed: true });
      return forms.get(form);
    }
    if (!record || record.status === 'completed') record = { id: newId(), at: Date.now(), status: 'draft' };
    const info = { key, record, marker };
    forms.set(form, info);
    saveInquiry(info);
    return info;
  }
  function syncContext(form, info) {
    const params = currentUrl.searchParams;
    const mapping = { inquiry_product_handle: 'product_handle', inquiry_product_title: 'product_title', inquiry_product_url: 'product_url', inquiry_product_image: 'product_image', inquiry_variant_id: 'variant_id' };
    Object.entries(mapping).forEach(([key, param]) => {
      if (!input(form, key)?.value && params.get(param)) set(form, key, params.get(param));
    });
    const handle = input(form, 'inquiry_product_handle')?.value;
    if (handle && !input(form, 'inquiry_product_url')?.value) set(form, 'inquiry_product_url', location.origin + '/products/' + encodeURIComponent(handle));
    if (handle && !info.marker.dataset.productHandle && !info.productRequested) {
      info.productRequested = true;
      if (!products.has(handle)) {
        const root = window.Shopify?.routes?.root || '/';
        products.set(handle, fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
          .then(response => response.ok ? response.json() : null).catch(() => null));
      }
      products.get(handle).then(product => {
        if (!product || !form.isConnected) return;
        if (!input(form, 'inquiry_product_title')?.value) set(form, 'inquiry_product_title', product.title);
        if (!input(form, 'inquiry_product_image')?.value) set(form, 'inquiry_product_image', product.featured_image || product.images?.[0] || '');
      });
    }
    if (info.marker.dataset.productHandle) {
      const root = form.closest('product-info') || form.closest('.shopify-section');
      const variant = root?.querySelector('form[action*="/cart/add"] [name="id"]');
      if (variant?.value) set(form, 'inquiry_variant_id', variant.value);
      else if (params.get('variant')) set(form, 'inquiry_variant_id', params.get('variant'));
    }
    const quantity = Array.from(form.elements).find(el => el.type !== 'hidden' && !el.disabled &&
      /quantity|qty/i.test(el.name || '') && (!['checkbox', 'radio'].includes(el.type) || el.checked));
    if (quantity) set(form, 'inquiry_quantity', quantity.value);
    else if (params.get('quantity')) set(form, 'inquiry_quantity', params.get('quantity'));
    set(form, 'inquiry_source', params.get('source') || info.marker.dataset.inquirySource);
    set(form, 'source_landing_page', params.get('landing_page') || (fresh(state.first) ? state.first.landing_page : location.href));
    set(form, 'page_url', location.href);
    const upload = form.querySelector('rp-contact-upload');
    if (upload?.entries) {
      const done = upload.entries.filter(entry => entry.state === 'done');
      set(form, 'file_upload_url', done.map(entry => entry.url).join('\n'));
      set(form, 'file_upload_name', done.map(entry => entry.file.name).join('\n'));
    }
  }
  function fill(form, submitter) {
    const info = initialize(form);
    if (!info || info.completed) return;
    if (!fresh(info.record, DAY)) {
      info.record = { id: newId(), at: Date.now(), status: 'draft' };
      saveInquiry(info);
    }
    set(form, 'inquiry_id', info.record.id);
    set(form, 'Inquiry ID', info.record.id);
    Object.entries(attribution()).forEach(([key, value]) => set(form, key, value));
    if (submitter) {
      const option = submitter.dataset.contactOption || (submitter.value === 'Request a call' ? 'book_call' : 'quote');
      set(form, 'continue_option', option);
    }
    syncContext(form, info);
  }
  function scan(root) {
    root.querySelectorAll('[data-rp-inquiry-fields]').forEach(marker => { const form = marker.closest('form'); if (form) fill(form); });
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('button[type="submit"], input[type="submit"]');
    if (button?.form) fill(button.form, button);
  }, true);
  document.addEventListener('submit', event => {
    fill(event.target, event.submitter);
    const info = forms.get(event.target);
    if (info && !info.completed) {
      info.record.status = 'pending';
      saveInquiry(info);
    }
  }, true);
  // formdata also covers native form.submit(), captcha resubmission and FormData.
  document.addEventListener('formdata', event => {
    fill(event.target);
    const info = forms.get(event.target);
    if (!info || info.completed) return;
    info.marker.querySelectorAll('input[name]').forEach(field => event.formData.set(field.name, field.value));
    const message = event.target.querySelector('textarea[name^="contact["]');
    const body = event.formData.get('contact[body]') ?? message?.value ?? '';
    const reference = 'Inquiry ID: ' + info.record.id;
    if (!String(body).includes(reference)) event.formData.set('contact[body]', String(body) + '\n\n' + reference);
  }, true);
  document.addEventListener('change', event => { if (event.target.form) fill(event.target.form); });
  document.addEventListener('rp:attachments-change', event => { const form = event.target.closest('form'); if (form) fill(form); });
  document.addEventListener('shopify:section:load', event => scan(event.target));
  window.addEventListener('pageshow', () => scan(document));
  window.restopackInquiry = { fill, scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document));
  else scan(document);
})();
